import { Link, useLocation } from "react-router-dom";
import { Sparkles, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function NotFound() {
  const loc = useLocation();
  const { user } = useAuth();

  return (
    <div
      className="min-h-screen flex flex-col justify-between select-none font-sans relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #6AA5E6 0%, #84B9EE 30%, #B0D4F9 60%, #DCEDFD 82%, #FFFFFF 100%)",
      }}
      data-testid="not-found-page"
    >
      {/* ── Top Header Bar Matching Reference Screenshot Exactly ── */}
      <header className="relative z-30 h-16 sm:h-20 px-6 sm:px-12 flex items-center justify-between w-full max-w-7xl mx-auto">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-2.5 group cursor-pointer">
          <div className="w-8 h-8 rounded-lg bg-white/95 flex items-center justify-center shadow-xs border border-white/50 group-hover:scale-105 transition-transform">
            <Sparkles className="w-4 h-4 text-blue-600" />
          </div>
          <span className="font-bold text-lg tracking-tight text-white drop-shadow-xs">
            LaunchDeck
          </span>
        </Link>

        {/* Top-Right Login / Dashboard Pill Button */}
        <div>
          {!user ? (
            <Link
              to="/signin"
              className="px-4 py-1.5 rounded-lg bg-white/95 hover:bg-white text-slate-800 text-xs font-semibold shadow-xs border border-white/70 transition-all hover:shadow-md cursor-pointer"
            >
              Login
            </Link>
          ) : (
            <Link
              to="/"
              className="px-4 py-1.5 rounded-lg bg-white/95 hover:bg-white text-slate-800 text-xs font-semibold shadow-xs border border-white/70 transition-all hover:shadow-md cursor-pointer"
            >
              Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* ── Main Center Scene ── */}
      <main className="relative z-20 flex-1 flex flex-col items-center justify-center px-4 -mt-2 sm:-mt-4">
        <div className="relative flex flex-col items-center justify-center max-w-4xl w-full">

          {/* ── Giant 4 0 4 Background Layer with Animated Character in Center ── */}
          <div className="relative w-full flex items-center justify-center select-none min-h-[280px] sm:min-h-[360px] md:min-h-[420px]">
            {/* The Huge "4 0 4" Numerals - All 3 letters visible with "0" framed behind the character */}
            <div className="flex items-center justify-center w-full pointer-events-none text-white font-black tracking-widest text-[140px] sm:text-[220px] md:text-[300px] leading-none select-none drop-shadow-sm opacity-90">
              <span className="transform -translate-x-4 sm:-translate-x-8">4</span>
              <span className="transform translate-x-0 opacity-75 scale-95">0</span>
              <span className="transform translate-x-4 sm:translate-x-8">4</span>
            </div>

            {/* ── Animated Center Mascot Character Standing In Front of "0" ── */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="relative flex flex-col items-center justify-center pointer-events-auto"
                style={{
                  animation: "mascotHover 3.5s ease-in-out infinite",
                }}
              >
                {/* 3D Character Image Render with High Fidelity */}
                <img
                  src="/character-404-transparent.png"
                  alt="404 Lost Mascot"
                  className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 object-contain drop-shadow-2xl transition-transform"
                  onError={(e) => {
                    // Fallback to jpg if png is loading
                    e.currentTarget.src = "/character-404.jpg";
                  }}
                />

                {/* Animated Directional Pointer Pointing Downwards towards Home Button */}
                <div
                  className="absolute -bottom-2 flex flex-col items-center pointer-events-none"
                  style={{
                    animation: "pointerPulse 1.8s ease-in-out infinite",
                  }}
                >
                  <div className="w-8 h-8 rounded-full bg-white/95 text-blue-600 flex items-center justify-center shadow-lg border border-blue-100">
                    <ArrowDown className="w-4 h-4 stroke-[3]" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Descriptive Text Matching Screenshot Exactly ── */}
          <div className="relative z-30 flex flex-col items-center text-center mt-3 sm:mt-5">
            {/* Heading 2 formatted with User-Mandated Montserrat Standard (Size 22, Weight 400, Italic 0) */}
            <h2
              className="heading-link font-montserrat-h2 text-slate-900 font-bold text-2xl sm:text-3xl mb-1.5 tracking-tight"
            >
              <Link to="/" className="hover:text-blue-600 transition-colors">
                Opps, I think we're lost
              </Link>
            </h2>

            {/* Subtitle */}
            <p className="text-xs sm:text-sm text-slate-600 font-normal mb-5 max-w-sm">
              Let's get you back somewhere familiar...
            </p>

            {/* Pill "Back to home" Button with pointing target animation */}
            <div className="relative group">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white hover:bg-slate-50 text-slate-800 hover:text-blue-600 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md border border-slate-200 transition-all duration-200 cursor-pointer"
              >
                <span className="text-slate-400 group-hover:-translate-x-1 transition-transform text-xs">
                  ←
                </span>
                <span>Back to home</span>
              </Link>
            </div>

            {/* Subtle Requested URL feedback */}
            <div className="mt-4 text-[11px] text-slate-400 font-sans">
              <span>Path: </span>
              <span className="text-slate-500 font-medium">{loc.pathname}</span>
            </div>
          </div>
        </div>
      </main>

      {/* ── Soft Cloud Base At Bottom ── */}
      <div className="relative z-10 w-full pointer-events-none select-none h-16 sm:h-24 overflow-hidden">
        <svg
          viewBox="0 0 1440 160"
          className="w-full h-full fill-white"
          preserveAspectRatio="none"
        >
          <path d="M0,96L48,112C96,128,192,160,288,154.7C384,149,480,107,576,96C672,85,768,107,864,122.7C960,139,1056,149,1152,144C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
        </svg>
      </div>

      {/* Inline styles for 404 Mascot animation */}
      <style>{`
        @keyframes mascotHover {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(-1deg);
          }
        }
        @keyframes pointerPulse {
          0%, 100% {
            transform: translateY(0px) scale(1);
            opacity: 0.9;
          }
          50% {
            transform: translateY(6px) scale(1.08);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
